import React from 'react';
import { Card, Row, Col, Statistic, Icon, Progress, Tag } from 'antd';

const EndpointAnalytics = ({ recentRequests, loading }) => {
  // Calculate endpoint statistics
  const calculateEndpointStats = () => {
    if (!recentRequests || recentRequests.length === 0) {
      return [];
    }

    const endpointMap = {};
    
    recentRequests.forEach(req => {
      const path = req.path || req.endpoint || 'unknown';
      
      if (!endpointMap[path]) {
        endpointMap[path] = {
          path,
          count: 0,
          totalResponseTime: 0,
          successCount: 0,
          errorCount: 0,
          totalCost: 0
        };
      }
      
      endpointMap[path].count++;
      endpointMap[path].totalResponseTime += (req.response_time || req.response_time_ms || 0);
      endpointMap[path].totalCost += (req.cost || req.cost_estimate || 0);
      
      const status = req.status || req.status_code;
      if (status === 200 || status === '200' || status === 'success') {
        endpointMap[path].successCount++;
      } else {
        endpointMap[path].errorCount++;
      }
    });
    
    // Convert to array and calculate averages
    const stats = Object.values(endpointMap).map(endpoint => ({
      ...endpoint,
      avgResponseTime: endpoint.count > 0 ? endpoint.totalResponseTime / endpoint.count : 0,
      successRate: endpoint.count > 0 ? (endpoint.successCount / endpoint.count) * 100 : 0
    }));
    
    // Sort by count descending
    return stats.sort((a, b) => b.count - a.count);
  };

  const endpointStats = calculateEndpointStats();
  const totalRequests = recentRequests?.length || 0;

  const getEndpointDisplayName = (path) => {
    if (!path || path === 'unknown') return 'Unknown Endpoint';
    
    if (path.includes('/openai/chat/completions')) return 'AI Insights';
    if (path.includes('suggest-questions')) return 'Smart Survey';
    if (path.includes('optimize-order')) return 'Question Ordering';
    if (path.includes('suggest-followups')) return 'Follow-up Suggestions';
    if (path.includes('survey-generation')) return 'Survey Generation';
    if (path.includes('openai')) return 'AI Insights';
    
    // Extract last part of path
    const parts = path.split('/').filter(p => p);
    return parts[parts.length - 1] || path;
  };

  const getEndpointIcon = (path) => {
    if (path.includes('/openai/chat/completions')) return 'bulb';
    if (path.includes('suggest-questions')) return 'form';
    if (path.includes('optimize-order')) return 'sort-ascending';
    if (path.includes('suggest-followups')) return 'branches';
    if (path.includes('openai')) return 'bulb';
    return 'api';
  };

  const getEndpointColor = (index) => {
    const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <Card loading={true}>
        <p>Loading endpoint analytics...</p>
      </Card>
    );
  }

  if (!endpointStats || endpointStats.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Icon type="api" style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
          <div>
            <p style={{ fontSize: 16, color: '#666' }}>No endpoint data available</p>
            <p style={{ fontSize: 14, color: '#999' }}>Make some AI service requests to see endpoint analytics</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        {endpointStats.slice(0, 6).map((endpoint, index) => {
          const percentage = totalRequests > 0 ? (endpoint.count / totalRequests) * 100 : 0;
          const color = getEndpointColor(index);
          
          return (
            <Col xs={24} sm={12} lg={8} key={endpoint.path}>
              <Card 
                hoverable
                style={{ 
                  borderRadius: 8,
                  borderLeft: `4px solid ${color}`,
                  height: '100%'
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <Icon 
                      type={getEndpointIcon(endpoint.path)} 
                      style={{ fontSize: 20, color: color, marginRight: 8 }} 
                    />
                    <span style={{ fontSize: 16, fontWeight: 600, color: '#262626' }}>
                      {getEndpointDisplayName(endpoint.path)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 28 }}>
                    {endpoint.path}
                  </div>
                </div>
                
                <div style={{ marginBottom: 16 }}>
                  <Row gutter={8}>
                    <Col span={12}>
                      <Statistic 
                        title="Requests" 
                        value={endpoint.count}
                        valueStyle={{ fontSize: 20, color: color }}
                        prefix={<Icon type="thunderbolt" />}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic 
                        title="Avg Time" 
                        value={endpoint.avgResponseTime.toFixed(0)}
                        suffix="ms"
                        valueStyle={{ fontSize: 20 }}
                      />
                    </Col>
                  </Row>
                </div>
                
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Usage Share</span>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{percentage.toFixed(1)}%</span>
                  </div>
                  <Progress 
                    percent={percentage} 
                    strokeColor={color}
                    showInfo={false}
                    size="small"
                  />
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Tag color={endpoint.successRate > 95 ? 'green' : endpoint.successRate > 85 ? 'orange' : 'red'}>
                      {endpoint.successRate.toFixed(1)}% success
                    </Tag>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#52c41a' }}>
                    ${endpoint.totalCost.toFixed(3)}
                  </div>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
      
      {endpointStats.length > 6 && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Tag color="blue">
            +{endpointStats.length - 6} more endpoints
          </Tag>
        </div>
      )}
    </div>
  );
};

export default EndpointAnalytics;
